import reviewsData from "@/public/reviews.json";

// Single source for the Google reviews shown on the site.
//
// public/reviews.json is written once at dev/build time by
// scripts/fetch-reviews.mjs and imported statically — same pattern as
// public/stats.json (see lib/siteStats.ts). Baked into the bundle at build
// time; never fetched from the browser, and this file never sees the Google
// client secret or any token.
//
// Upstream: the Cloudflare Worker at the repo root (wrangler.toml +
// worker.js) runs a daily cron that refreshes a Google OAuth token, calls
// the Business Profile API, stores the result in Workers KV, then triggers
// a Pages rebuild — which re-runs fetch-reviews.mjs and refreshes this file.
//
// See GOOGLE_REVIEWS_DEVELOPER_GUIDE.md for the full setup.

export type GoogleReview = {
  reviewer_name: string;
  reviewer_photo_url: string | null;
  /** Google's enum, e.g. "FIVE" — not a number. See starRatingToNumber(). */
  star_rating: string | null;
  comment: string;
  create_time: string | null;
  update_time: string | null;
  reply: { comment: string; update_time: string } | null;
  /**
   * Which Vilva Networks office this review belongs to — Kodungaiyur or
   * Ambattur. Both locations share the exact same title on Google, so this
   * label is assigned by worker.js from the one-time setup lookup, not by
   * Google's API response.
   */
  location_label: string;
};

export type GoogleReviewLocation = { label: string; view_on_google_url: string };

type ReviewsSnapshot = {
  reviews: GoogleReview[];
  average_rating: number | null;
  total_review_count: number | null;
  locations: GoogleReviewLocation[];
  updated_at: string | null;
};

const raw = reviewsData as unknown as ReviewsSnapshot;

export const GOOGLE_REVIEWS: GoogleReview[] = raw.reviews ?? [];
export const GOOGLE_AVERAGE_RATING: number | null = raw.average_rating ?? null;
export const GOOGLE_TOTAL_REVIEW_COUNT: number | null = raw.total_review_count ?? null;
/** One entry per office (Kodungaiyur, Ambattur) — reviews above combine both. */
export const GOOGLE_REVIEW_LOCATIONS: GoogleReviewLocation[] = raw.locations ?? [];
export const GOOGLE_REVIEWS_UPDATED_AT: string | null = raw.updated_at ?? null;

const STAR_WORDS: Record<string, number> = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5,
};

/** Google's Business Profile API returns stars as the word "FIVE", not 5. */
export function starRatingToNumber(starRating: string | null): number {
  if (!starRating) return 0;
  return STAR_WORDS[starRating] ?? 0;
}
